/**
 * Section Scroll Animations
 * Adds fade-in animations to sections as they scroll into view
 */

/**
 * Initializes scroll-based animations for all sections
 * Only runs when scroll-animations-enabled class is on body
 */
const initSectionScrollAnimations = () => {
  // Check if animations are enabled
  if (!document.body.classList.contains('scroll-animations-enabled')) return;

  // Respect user's reduced motion preference
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Select all section wrappers, excluding header and footer groups
  const sections = document.querySelectorAll('main .section-wrapper');
  if (sections.length === 0) return;

  /**
   * IntersectionObserver callback
   * @param {IntersectionObserverEntry[]} entries
   * @param {IntersectionObserver} observer
   */
  const handleIntersection = (entries, observer) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;

      const section = entry.target;

      // Add visible class to trigger animation
      section.classList.add('section-visible');

      // Stop observing this section (animation is one-time)
      observer.unobserve(section);
    }
  };

  // Create observer with threshold for triggering animation
  const observer = new IntersectionObserver(handleIntersection, {
    root: null,
    rootMargin: '0px 0px -50px 0px',
    threshold: 0.1
  });

  // Mark sections that are already in view on page load as visible immediately
  const markInitiallyVisibleSections = () => {
    for (const section of sections) {
      const rect = section.getBoundingClientRect();
      const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

      if (isInViewport) {
        // Section is already visible, mark it immediately
        section.classList.add('section-visible');
      } else {
        // Section is below viewport, observe it for animation
        observer.observe(section);
      }
    }
  };

  // Run after a brief delay to ensure layout is complete
  requestAnimationFrame(() => {
    requestAnimationFrame(markInitiallyVisibleSections);
  });
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSectionScrollAnimations);
} else {
  initSectionScrollAnimations();
}

// Re-initialize on Shopify section events (theme editor)
document.addEventListener('shopify:section:load', initSectionScrollAnimations);
